import { CredentialFileRendition } from "@api/shared-runtime/enums";
import { trpcClient, type RouterOutputs } from "@/trpc";

export type CredentialFileUrl =
  RouterOutputs["credentialFiles"]["getVersionFileUrls"][number];

const HEIC_EXTENSION = "heic";

/**
 * Legacy credential files are opened through API-issued URLs, never by the
 * browser signing a storage path with its own JWT: the API checks the caller
 * may see this user's documents and that every version is theirs and scanned.
 * `switchUserId` is the documents' owner; omit it for the caller's own.
 */
export async function getCredentialFileUrls({
  switchUserId,
  organizationBusinessUnitId,
  versions,
}: {
  switchUserId?: string;
  organizationBusinessUnitId?: string;
  versions: { versionId: string; rendition: CredentialFileRendition }[];
}): Promise<CredentialFileUrl[]> {
  return trpcClient.credentialFiles.getVersionFileUrls.mutate({
    switchUserId,
    organizationBusinessUnitId,
    versions,
  });
}

/**
 * Browsers cannot draw HEIC, so its preview uses the JPEG the server wrote
 * beside the original. Downloads always use the original.
 */
export function previewRenditionForFileName(
  fileName: string,
): CredentialFileRendition {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (extension === HEIC_EXTENSION) {
    return CredentialFileRendition.Jpeg;
  }
  return CredentialFileRendition.Original;
}

/** Read a file the API just signed. A missing object has no URL. */
export async function fetchCredentialFile(
  file: CredentialFileUrl,
): Promise<Blob> {
  if (!file.url) {
    throw new Error("This document is not available.");
  }
  const response = await fetch(file.url, { credentials: "omit" });
  if (!response.ok) {
    throw new Error("Could not retrieve this document.");
  }
  return response.blob();
}
